import { useState, useEffect, useMemo } from 'react';
import { Table, Select, message, Typography, Card, Alert, List } from 'antd';
import { getPromotionPaths, updatePromotionPath } from '../services/promotionService';
import { useAcademicYear } from '../context/AcademicYearContext';
import useResponsive from '../hooks/useResponsive';

const { Title, Text } = Typography;
const { Option } = Select;

const PromotionPathsPage = () => {
    const { isMobile } = useResponsive();
    const { currentYear } = useAcademicYear();
    const [currentClasses, setCurrentClasses] = useState([]);
    const [futureClasses, setFutureClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState(null); // Tracks which row is currently being saved

    useEffect(() => {
        if (currentYear) {
            fetchData();
        }
    }, [currentYear]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const { currentYearClasses, futureYearClasses } = await getPromotionPaths();
            setCurrentClasses(currentYearClasses.map(c => ({ ...c, key: c.id })));
            setFutureClasses(futureYearClasses);
        } catch (error) {
            message.error('Failed to load promotion data.');
        } finally {
            setLoading(false);
        }
    };

    const handlePathChange = async (classId, promotesToClassId) => {
        setSavingId(classId);
        try {
            await updatePromotionPath(classId, promotesToClassId);
            // Update local state for immediate feedback
            setCurrentClasses(prev => prev.map(c =>
                c.id === classId ? { ...c, promotes_to_class_id: promotesToClassId } : c
            ));
            message.success('Promotion path saved!');
        } catch (error) {
            message.error('Failed to save path.');
        } finally {
            setSavingId(null);
        }
    };

    const futureClassOptions = useMemo(() => {
        const options = futureClasses.map(fc => ({
            value: fc.id,
            label: fc.name
        }));
        // Add an option for graduating classes
        options.unshift({ value: null, label: 'Graduates / No Promotion' });
        return options;
    }, [futureClasses]);

    const columns = [
        {
            title: 'Current Year Class',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Promotes To (Next Year)',
            dataIndex: 'promotes_to_class_id',
            key: 'promotes_to_class_id',
            render: (promotesToClassId, record) => (
                <Select
                    showSearch
                    style={{ width: '100%' }}
                    placeholder="Select next year's class"
                    value={promotesToClassId}
                    loading={savingId === record.id}
                    onChange={(value) => handlePathChange(record.id, value)}
                    optionFilterProp="label"
                    options={futureClassOptions}
                    allowClear
                />
            )
        },
    ];

    const renderMobileList = () => (
        <List
            loading={loading}
            dataSource={currentClasses}
            renderItem={(item) => (
                <List.Item>
                    <Card style={{ width: '100%' }}>
                        <Text strong>{item.name}</Text>
                        <Select
                            showSearch
                            style={{ width: '100%', marginTop: 8 }}
                            placeholder="Select next year's class"
                            value={item.promotes_to_class_id}
                            loading={savingId === item.id}
                            onChange={(value) => handlePathChange(item.id, value)}
                            optionFilterProp="label"
                            options={futureClassOptions}
                            allowClear
                        />
                    </Card>
                </List.Item>
            )}
        />
    );

    return (
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
            <Title level={2}>Promotion Paths</Title>
            <Alert
                message="Set Promotion Paths"
                description="For each class in the current year, select the class it will promote to in the next academic year. This is required before running the 'Promote to New Year' process."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />
            <Card>
                {futureClasses.length === 0 && !loading && (
                    <Alert
                        message="Next Academic Year Not Found"
                        description="You must first create the next academic year (e.g., 2026-2027) in 'Academic Year Management' before you can set promotion paths."
                        type="warning"
                        showIcon
                    />
                )}
                {isMobile ? renderMobileList() : (
                    <Table
                        loading={loading}
                        dataSource={currentClasses}
                        columns={columns}
                        pagination={false}
                        bordered
                    />
                )}
            </Card>
        </div>
    );
};

export default PromotionPathsPage;